import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { STATUSES, STATUS_LABELS, PRIORITIES, fmtDateTime } from "@/lib/constants";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { SLABadge } from "@/components/SLABadge";
import { Plus, X, LayoutGrid, List, Search, History, RotateCcw, Trash2, Pencil, Layers } from "lucide-react";
import { ExportButton } from "@/components/ExportButton";
import { DeleteButton } from "@/components/DeleteButton";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const KANBAN_COLUMNS = ["open", "assigned", "accepted", "on_the_way", "reached", "in_progress", "waiting_parts", "completed", "closed"];

function BulkTicketModal({ customers, engineers, onClose, onCreated }) {
  const [f, setF] = useState({ customer_id: "", priority: "medium", call_type: "", branch_location: "", contact_person: "", contact_phone: "", assigned_engineer_id: "" });
  const [lines, setLines] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false); const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const complaints = lines.split("\n").map((l) => l.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...f, complaints };
      if (!payload.assigned_engineer_id) delete payload.assigned_engineer_id;
      if (!payload.call_type) delete payload.call_type;
      const r = await api.post("/tickets/bulk", payload);
      setDone(r.data);
    } catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl sharp-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> Bulk tickets — one customer</h3>
          <button data-testid="bulk-ticket-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        {done ? (
          <div className="p-5">
            <div data-testid="bulk-ticket-success" className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-600">
              {done.count} service call{done.count !== 1 ? "s" : ""} created successfully.
            </div>
            <ul className="mt-3 max-h-60 space-y-1 overflow-y-auto text-sm">
              {done.tickets.map((t) => (
                <li key={t.id} className="rounded-md border border-border/60 px-3 py-1.5"><b>{t.ticket_no}</b> — {t.complaint}</li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end">
              <button data-testid="bulk-ticket-done" onClick={onCreated} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Customer *</label>
              <select data-testid="bulk-ticket-customer" required value={f.customer_id} onChange={set("customer_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">— Select customer —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Priority</label>
              <select data-testid="bulk-ticket-priority" value={f.priority} onChange={set("priority")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Call type</label>
              <select data-testid="bulk-ticket-call-type" value={f.call_type} onChange={set("call_type")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">— Select call type —</option>
                {CALL_TYPES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Branch / Location</label>
              <input data-testid="bulk-ticket-branch" value={f.branch_location} onChange={set("branch_location")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Assign engineer (all tickets)</label>
              <select data-testid="bulk-ticket-engineer" value={f.assigned_engineer_id} onChange={set("assigned_engineer_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Leave unassigned</option>
                {engineers.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Contact person</label>
              <input data-testid="bulk-ticket-contact-person" value={f.contact_person} onChange={set("contact_person")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Contact phone</label>
              <input data-testid="bulk-ticket-contact-phone" value={f.contact_phone} onChange={set("contact_phone")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Complaints — one ticket per line * ({complaints.length} ticket{complaints.length !== 1 ? "s" : ""})</label>
              <textarea data-testid="bulk-ticket-complaints" required rows={6} value={lines} onChange={(e) => setLines(e.target.value)}
                placeholder={"Printer not working - Accounts dept\nUPS battery replacement - Server room\nCCTV camera 3 offline - Main gate"}
                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm font-semibold hover:bg-secondary">Cancel</button>
              <button data-testid="bulk-ticket-submit" disabled={busy || complaints.length === 0} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                {busy ? "Creating…" : `Create ${complaints.length || ""} ticket${complaints.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export const CALL_TYPES = [
  { key: "installation", label: "Installation Call" },
  { key: "amc",          label: "AMC Call" },
  { key: "onetime",      label: "OneTime Call" },
  { key: "tpm_vendor",   label: "TPM Vendor Call" },
  { key: "chargeable",   label: "Chargeable Call" },
  { key: "warranty",     label: "Warranty Call" },
];
export const CALL_TYPE_LABELS = Object.fromEntries(CALL_TYPES.map((c) => [c.key, c.label]));

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [view, setView] = useState("board");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [slaFilter, setSlaFilter] = useState("");
  const [callTypeFilter, setCallTypeFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleting, setDeleting] = useState(null); // { id, ticket_no }
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeHistory = user && ["super_admin", "company_admin"].includes(user.role);

  const load = async () => {
    try {
      const [t, c, e] = await Promise.all([
        api.get("/tickets"),
        api.get("/customers"),
        api.get("/users/engineers"),
      ]);
      setTickets(t.data); setCustomers(c.data); setEngineers(e.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      if (statusFilter && t.status !== statusFilter) return false;
      if (slaFilter && t.sla_status !== slaFilter) return false;
      if (callTypeFilter && (t.call_type || "unspecified") !== callTypeFilter) return false;
      if (branchFilter && (t.branch_location || "unspecified") !== branchFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${t.ticket_no} ${t.complaint} ${t.customer?.company_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, search, statusFilter, slaFilter, callTypeFilter, branchFilter]);

  const branchOptions = useMemo(() => {
    const set = new Set();
    tickets.forEach((t) => { if (t.branch_location) set.add(t.branch_location); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [tickets]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(KANBAN_COLUMNS.map((s) => [s, []]));
    filtered.forEach((t) => { if (g[t.status]) g[t.status].push(t); });
    return g;
  }, [filtered]);

  const onCreated = () => { setCreating(false); load(); };

  const filteredIds = filtered.map((t) => t.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) filteredIds.forEach((id) => n.delete(id));
    else filteredIds.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    const reason = window.prompt(`Delete ${ids.length} ticket(s). Please enter a short reason (visible in Delete History):`);
    if (reason === null) return; // user cancelled
    setBulkBusy(true);
    const qs = reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : "";
    const ok = await bulkDelete(ids, (id) => api.delete(`/tickets/${id}${qs}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < ids.length) setErr(`${ids.length - ok} deletion(s) failed.`);
  };

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Operations</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Service Calls</h1>
          <p className="mt-1 text-sm text-muted-foreground">Kanban pipeline for every ticket, from open to closed.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-border/70">
            <button
              data-testid="tickets-view-board"
              onClick={() => setView("board")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium ${view === "board" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Board
            </button>
            <button
              data-testid="tickets-view-list"
              onClick={() => setView("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
            >
              <List className="h-3.5 w-3.5" /> List
            </button>
          </div>
          <button
            data-testid="tickets-create-button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" /> New service call
          </button>
          <button
            data-testid="tickets-bulk-create-button"
            onClick={() => setBulkCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary"
          >
            <Layers className="h-3.5 w-3.5" /> Bulk tickets
          </button>
          {canSeeHistory && (
            <button
              data-testid="tickets-history-open"
              onClick={() => setHistoryOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary"
            >
              <History className="h-3.5 w-3.5" /> Delete history
            </button>
          )}
          <ExportButton resource="tickets" testid="tickets-export" />
        </div>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="tickets-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ticket #, complaint, customer…"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <select
          data-testid="tickets-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select
          data-testid="tickets-sla-filter"
          value={slaFilter}
          onChange={(e) => setSlaFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All SLAs</option>
          <option value="breached">SLA breached</option>
          <option value="at_risk">SLA at risk</option>
          <option value="on_track">On track</option>
        </select>
        <select
          data-testid="tickets-call-type-filter"
          value={callTypeFilter}
          onChange={(e) => setCallTypeFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All call types</option>
          {CALL_TYPES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          <option value="unspecified">Not set</option>
        </select>
        <select
          data-testid="tickets-branch-filter"
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All branches</option>
          {branchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
          <option value="unspecified">No branch set</option>
        </select>
      </div>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      {view === "board" ? (
        <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-thin">
          {KANBAN_COLUMNS.map((col) => (
            <div key={col} data-testid={`kanban-col-${col}`} className="min-w-[280px] flex-1">
              <div className="mb-2 flex items-center justify-between px-1">
                <StatusBadge status={col} />
                <span className="text-xs text-muted-foreground">{grouped[col].length}</span>
              </div>
              <div className="space-y-2">
                {grouped[col].map((t) => (
                  <button
                    key={t.id}
                    data-testid={`ticket-card-${t.ticket_no}`}
                    onClick={() => navigate(`/app/tickets/${t.id}`)}
                    className="w-full text-left sharp-card p-3 transition-all duration-150 hover:-translate-y-0.5 hover:border-primary/50"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="font-mono text-[11px] text-muted-foreground">{t.ticket_no}</span>
                      <span className="flex items-center gap-1">
                        <PriorityBadge priority={t.priority} />
                        <span
                          data-testid={`ticket-card-edit-${t.ticket_no}`}
                          role="button"
                          title="Edit ticket"
                          onClick={(e) => { e.stopPropagation(); setEditingTicket(t); }}
                          className="rounded-md border border-border/70 p-1 text-muted-foreground hover:text-primary"
                        >
                          <Pencil className="h-3 w-3" />
                        </span>
                      </span>
                    </div>
                    <p className="line-clamp-2 text-sm font-medium">{t.complaint}</p>
                    {t.call_type && (
                      <span className="mt-1 inline-flex rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">{CALL_TYPE_LABELS[t.call_type] || t.call_type}</span>
                    )}
                    <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                      <p className="truncate">{t.customer?.company_name || "—"}</p>
                      <p className="truncate">{t.engineer?.name ? `Assigned · ${t.engineer.name}` : "Unassigned"}</p>
                    </div>
                  </button>
                ))}
                {grouped[col].length === 0 && (
                  <div className="rounded-md border border-dashed border-border/60 py-6 text-center text-[11px] text-muted-foreground">
                    Empty
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="sharp-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/30">
              <tr className="text-left">
                <th className="w-10 px-4 py-2">
                  <RowCheckbox testid="tickets-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
                </th>
                <th className="px-4 py-2 label-eyebrow">Ticket</th>
                <th className="px-4 py-2 label-eyebrow">Customer</th>
                <th className="px-4 py-2 label-eyebrow">Complaint</th>
                <th className="px-4 py-2 label-eyebrow">Branch/Location</th>
                <th className="px-4 py-2 label-eyebrow">Call Type</th>
                <th className="px-4 py-2 label-eyebrow">Priority</th>
                <th className="px-4 py-2 label-eyebrow">Status</th>
                <th className="px-4 py-2 label-eyebrow">Engineer</th>
                <th className="px-4 py-2 label-eyebrow">Created</th>
                <th className="px-4 py-2 label-eyebrow">Created by</th>
                <th className="px-4 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => navigate(`/app/tickets/${t.id}`)}
                  data-testid={`ticket-row-${t.ticket_no}`}
                  className="cursor-pointer border-b border-border/40 hover:bg-secondary/40"
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <RowCheckbox testid={`ticket-select-${t.ticket_no}`} checked={selected.has(t.id)} onChange={() => toggleOne(t.id)} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{t.ticket_no}</td>
                  <td className="px-4 py-3">{t.customer?.company_name || "—"}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{t.complaint}</td>
                  <td className="px-4 py-3 max-w-[160px] truncate text-xs text-muted-foreground">{t.branch_location || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {t.call_type ? (
                      <span className="inline-flex rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{CALL_TYPE_LABELS[t.call_type] || t.call_type}</span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={t.status} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{t.engineer?.name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    {t.created_by_name ? (
                      <>
                        <span className="text-foreground">{t.created_by_name}</span>
                        {t.created_by_role && <span className="ml-1 text-[10px] uppercase tracking-wider text-muted-foreground">· {t.created_by_role.replace("_", " ")}</span>}
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button
                        data-testid={`ticket-edit-${t.ticket_no}`}
                        onClick={() => setEditingTicket(t)}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`ticket-delete-${t.ticket_no}`}
                        onClick={() => setDeleting({ id: t.id, ticket_no: t.ticket_no })}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-xs text-muted-foreground">No tickets found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <BulkBar count={selected.size} entityLabel="tickets" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && (
        <TicketCreateModal
          customers={customers}
          engineers={engineers}
          onClose={() => setCreating(false)}
          onCreated={onCreated}
        />
      )}

      {editingTicket && (
        <TicketEditModal
          ticket={editingTicket}
          engineers={engineers}
          onClose={() => setEditingTicket(null)}
          onSaved={() => { setEditingTicket(null); load(); }}
        />
      )}

      {bulkCreating && (
        <BulkTicketModal
          customers={customers}
          engineers={engineers}
          onClose={() => setBulkCreating(false)}
          onCreated={() => { setBulkCreating(false); load(); }}
        />
      )}

      {historyOpen && (
        <DeleteHistoryModal
          canPurge={user?.role === "super_admin"}
          onClose={() => setHistoryOpen(false)}
          onRestored={() => { setHistoryOpen(false); load(); }}
        />
      )}

      {deleting && (
        <DeleteReasonModal
          ticket={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={async (reason) => {
            try {
              const qs = reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : "";
              await api.delete(`/tickets/${deleting.id}${qs}`);
              setDeleting(null); await load();
            } catch (e) { setErr(formatApiError(e)); }
          }}
        />
      )}
    </div>
  );
}

function TicketEditModal({ ticket, engineers, onClose, onSaved }) {
  const [form, setForm] = useState({
    complaint: ticket.complaint || "",
    description: ticket.description || "",
    priority: ticket.priority || "medium",
    call_type: ticket.call_type || "",
    branch_location: ticket.branch_location || "",
    contact_person: ticket.contact_person || "",
    contact_phone: ticket.contact_phone || "",
    assigned_engineer_id: ticket.assigned_engineer_id || "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      // Explicitly send null to unassign the engineer (backend reopens the call)
      if (!form.assigned_engineer_id) payload.assigned_engineer_id = null;
      await api.patch(`/tickets/${ticket.id}`, payload);
      onSaved();
    } catch (er) { setErr(formatApiError(er)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl sharp-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">Edit {ticket.ticket_no}</h3>
          <button data-testid="ticket-edit-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Complaint *</label>
            <input data-testid="ticket-edit-complaint" required value={form.complaint} onChange={onChange("complaint")} className="input" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Priority</label>
            <select data-testid="ticket-edit-priority" value={form.priority} onChange={onChange("priority")} className="input">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Call type</label>
            <select data-testid="ticket-edit-call-type" value={form.call_type} onChange={onChange("call_type")} className="input">
              <option value="">— Select call type —</option>
              {CALL_TYPES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Branch / Location</label>
            <input data-testid="ticket-edit-branch" value={form.branch_location} onChange={onChange("branch_location")} className="input" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Assign engineer</label>
            <select data-testid="ticket-edit-engineer" value={form.assigned_engineer_id} onChange={onChange("assigned_engineer_id")} className="input">
              <option value="">— Unassigned —</option>
              {engineers.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Contact person</label>
            <input data-testid="ticket-edit-contact-person" value={form.contact_person} onChange={onChange("contact_person")} className="input" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Contact phone</label>
            <input data-testid="ticket-edit-contact-phone" value={form.contact_phone} onChange={onChange("contact_phone")} className="input" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Description</label>
            <textarea data-testid="ticket-edit-description" value={form.description} onChange={onChange("description")} rows={2} className="input resize-none" />
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="ticket-edit-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteReasonModal({ ticket, onClose, onDeleted }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [presets, setPresets] = useState(["Duplicate", "Test entry", "Wrong customer", "Customer withdrew"]);
  useEffect(() => {
    api.get("/settings/delete-reason-chips")
      .then((r) => { if (r.data?.chips?.length) setPresets(r.data.chips); })
      .catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    await onDeleted(reason);
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <p className="label-eyebrow text-destructive">Delete service call</p>
            <h3 className="mt-1 font-heading text-lg font-semibold">
              Reason for deleting <span className="font-mono text-sm">{ticket.ticket_no}</span>?
            </h3>
          </div>
          <button type="button" data-testid="ticket-delete-cancel" onClick={onClose} className="rounded-md border border-border/70 p-1.5">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-5">
          <p className="text-xs text-muted-foreground">
            The ticket will be archived to <b>Delete History</b> along with the reason, your name and timestamp. It can be restored by an admin at any time.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {presets.map((preset) => (
              <button
                type="button"
                key={preset}
                data-testid={`ticket-delete-chip-${preset.toLowerCase().replace(/ /g, "-")}`}
                onClick={() => setReason(preset)}
                className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${reason === preset ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}
              >
                {preset}
              </button>
            ))}
          </div>
          <textarea
            data-testid="ticket-delete-reason"
            required
            autoFocus
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Tap a chip above or write your own reason…"
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <p className="text-[10px] text-muted-foreground text-right">{reason.length}/500</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button
              data-testid="ticket-delete-confirm"
              type="submit"
              disabled={!reason.trim() || busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
            >
              <Trash2 className="h-3.5 w-3.5" /> {busy ? "Deleting…" : "Delete ticket"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function DeleteHistoryModal({ canPurge, onClose, onRestored }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/tickets/deleted");
      setRows(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id, ticketNo) => {
    if (!window.confirm(`Restore ${ticketNo} back to active tickets?`)) return;
    try { await api.post(`/tickets/deleted/${id}/restore`); await load(); onRestored(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const purge = async (id, ticketNo) => {
    if (!window.confirm(`Permanently purge ${ticketNo}? This cannot be undone.`)) return;
    try { await api.delete(`/tickets/deleted/${id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-5xl sharp-card max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">Audit</p>
            <h3 className="mt-1 font-heading text-lg font-semibold flex items-center gap-2">
              <History className="h-4 w-4" /> Delete history
            </h3>
            <p className="text-xs text-muted-foreground">Every service call that was deleted, newest first.</p>
          </div>
          <button data-testid="tickets-history-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {err && <div className="mx-5 mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          {loading ? (
            <p className="px-5 py-10 text-center text-xs text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-10 text-center text-xs text-muted-foreground">No deleted tickets yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-secondary/50 text-left">
                <tr>
                  <th className="px-4 py-2 label-eyebrow">Ticket</th>
                  <th className="px-4 py-2 label-eyebrow">Complaint</th>
                  <th className="px-4 py-2 label-eyebrow">Customer</th>
                  <th className="px-4 py-2 label-eyebrow">Created</th>
                  <th className="px-4 py-2 label-eyebrow">Deleted</th>
                  <th className="px-4 py-2 label-eyebrow">Reason</th>
                  <th className="px-4 py-2 label-eyebrow">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`deleted-row-${r.ticket_no}`} className="border-b border-border/40">
                    <td className="px-4 py-2 font-mono text-xs">{r.ticket_no}</td>
                    <td className="px-4 py-2 max-w-[240px] truncate">{r.complaint}</td>
                    <td className="px-4 py-2 text-xs">{r.customer_name || "—"}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      <div>{fmtDateTime(r.created_at)}</div>
                      {r.created_by_name && <div className="text-[10px]">by {r.created_by_name}</div>}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <div className="text-rose-600">{fmtDateTime(r.deleted_at)}</div>
                      {r.deleted_by_name && <div className="text-[10px] text-muted-foreground">by {r.deleted_by_name}</div>}
                    </td>
                    <td className="px-4 py-2 max-w-[220px] text-xs">
                      {r.deleted_reason ? (
                        <p className="italic text-muted-foreground" title={r.deleted_reason}>“{r.deleted_reason}”</p>
                      ) : (
                        <span className="text-muted-foreground/60">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button
                          data-testid={`deleted-restore-${r.ticket_no}`}
                          onClick={() => restore(r.id, r.ticket_no)}
                          className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-600 hover:bg-emerald-500/20"
                          title="Restore"
                        >
                          <RotateCcw className="h-3 w-3" /> Restore
                        </button>
                        {canPurge && (
                          <button
                            data-testid={`deleted-purge-${r.ticket_no}`}
                            onClick={() => purge(r.id, r.ticket_no)}
                            className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:border-destructive hover:text-destructive"
                            title="Purge permanently"
                          >
                            <Trash2 className="h-3 w-3" /> Purge
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function TicketCreateModal({ customers: initialCustomers, engineers, onClose, onCreated }) {
  const [customers, setCustomers] = useState(initialCustomers);
  const [creatingCust, setCreatingCust] = useState(false);
  const [form, setForm] = useState({
    customer_id: "", equipment_id: "", contact_person: "", contact_phone: "",
    priority: "medium", call_type: "", branch_location: "", complaint: "", description: "", sla_hours: 24,
    assigned_engineer_id: "", serial_no: "", model_no: "",
  });
  const [equipment, setEquipment] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form.customer_id) { setEquipment([]); return; }
    (async () => {
      try {
        const r = await api.get(`/equipment?customer_id=${form.customer_id}`);
        setEquipment(r.data);
      } catch { /* ignore */ }
    })();
  }, [form.customer_id]);

  // Auto-fill serial + model when equipment is picked
  useEffect(() => {
    if (!form.equipment_id) return;
    const eq = equipment.find((e) => e.id === form.equipment_id);
    if (eq) {
      setForm((f) => ({
        ...f,
        serial_no: eq.serial_number || f.serial_no,
        model_no: eq.model || f.model_no,
      }));
    }
  }, [form.equipment_id, equipment]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = { ...form, sla_hours: Number(form.sla_hours) || 24 };
      if (!payload.branch_location) delete payload.branch_location;
      if (!payload.equipment_id) delete payload.equipment_id;
      if (!payload.assigned_engineer_id) delete payload.assigned_engineer_id;
      if (!payload.serial_no) delete payload.serial_no;
      if (!payload.model_no) delete payload.model_no;
      if (!payload.call_type) delete payload.call_type;
      await api.post("/tickets", payload);
      onCreated();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl sharp-card">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">New service call</p>
            <h3 className="mt-1 font-heading text-lg font-semibold">Log a new ticket</h3>
          </div>
          <button data-testid="ticket-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:bg-secondary">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <Field label="Customer *">
            <div className="flex gap-1">
              <select required data-testid="ticket-form-customer" value={form.customer_id} onChange={onChange("customer_id")} className="input flex-1">
                <option value="">Select customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              <button type="button" data-testid="ticket-form-add-customer" onClick={() => setCreatingCust(true)}
                title="Add new customer"
                className="rounded-md border border-border/70 px-2 text-xs font-semibold text-primary hover:bg-primary/10">
                + Add
              </button>
            </div>
          </Field>
          <Field label="Equipment">
            <select data-testid="ticket-form-equipment" value={form.equipment_id} onChange={onChange("equipment_id")} className="input">
              <option value="">— None —</option>
              {equipment.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.model || "no model"})</option>)}
            </select>
          </Field>
          <Field label="Serial No">
            <input data-testid="ticket-form-serial-no" value={form.serial_no} onChange={onChange("serial_no")} className="input" placeholder="e.g. SN-2426-119" />
          </Field>
          <Field label="Model No">
            <input data-testid="ticket-form-model-no" value={form.model_no} onChange={onChange("model_no")} className="input" placeholder="e.g. HP-LJ-M404dn" />
          </Field>
          <Field label="Contact person">
            <input data-testid="ticket-form-contact-person" value={form.contact_person} onChange={onChange("contact_person")} className="input" />
          </Field>
          <Field label="Contact phone">
            <input data-testid="ticket-form-contact-phone" value={form.contact_phone} onChange={onChange("contact_phone")} className="input" />
          </Field>
          <Field label="Priority">
            <select data-testid="ticket-form-priority" value={form.priority} onChange={onChange("priority")} className="input">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Call type">
            <select data-testid="ticket-form-call-type" value={form.call_type} onChange={onChange("call_type")} className="input">
              <option value="">— Select call type —</option>
              {CALL_TYPES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Branch / Location">
            <input data-testid="ticket-form-branch-location" value={form.branch_location} onChange={onChange("branch_location")} placeholder="e.g. Head Office, Pune Branch" className="input" />
          </Field>
          <Field label="Assign engineer" full>
            <select data-testid="ticket-form-engineer" value={form.assigned_engineer_id} onChange={onChange("assigned_engineer_id")} className="input">
              <option value="">Leave unassigned</option>
              {engineers.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.email}</option>)}
            </select>
          </Field>
          <Field label="Complaint *" full>
            <input required data-testid="ticket-form-complaint" value={form.complaint} onChange={onChange("complaint")} className="input" />
          </Field>
          <Field label="Description" full>
            <textarea data-testid="ticket-form-description" value={form.description} onChange={onChange("description")} rows={3} className="input resize-none" />
          </Field>

          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="ticket-form-submit" disabled={busy} type="submit" className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Creating…" : "Create ticket"}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input { width:100%; border:1px solid hsl(var(--input)); background:hsl(var(--background)); border-radius:6px; padding:0.5rem 0.75rem; font-size:0.875rem; outline:none; } .input:focus { box-shadow: 0 0 0 2px hsl(var(--ring)/.35); }`}</style>
      {creatingCust && (
        <QuickCustomerModal
          onClose={() => setCreatingCust(false)}
          onSaved={async (newCust) => {
            setCreatingCust(false);
            // Refresh customer list and select the new one
            try {
              const r = await api.get("/customers");
              setCustomers(r.data);
              setForm((f) => ({ ...f, customer_id: newCust.id }));
            } catch { /* ignore */ }
          }}
        />
      )}
    </div>
  );
}

function QuickCustomerModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ company_name: "", contact_person: "", email: "", mobile: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const r = await api.post("/customers", form); onSaved(r.data); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">Quick add customer</h3>
          <button data-testid="quick-customer-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <div>
            <label className="label-eyebrow mb-1.5 block">Company name *</label>
            <input data-testid="quick-customer-name" required value={form.company_name} onChange={set("company_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Contact person</label>
            <input data-testid="quick-customer-contact" value={form.contact_person} onChange={set("contact_person")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Email</label>
            <input data-testid="quick-customer-email" type="email" value={form.email} onChange={set("email")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Mobile</label>
            <input data-testid="quick-customer-mobile" value={form.mobile} onChange={set("mobile")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="quick-customer-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Creating…" : "Create & select"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="label-eyebrow mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
